/**
 * KAN-806: save of the client card ("Сохранить" on ClientDetailPage).
 *
 * One request: PATCH /clients/<id>/ (clientsApi.update). The body holds the
 * common fields (phone, name, email, notes) when they were edited, plus
 * telegram_username when it differs from the saved one after normalization
 * ("" clears it). The numeric telegram_id is never sent.
 *
 * The server saves the whole body or nothing: on a 400 (for example an
 * invalid username) the user gets the server text and the form stays as typed.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { clientsApi } from '@/services/api/clients';
import { ATTENTION_QUERY_KEY } from '@/services/api/queryKeys';
import { formatFieldErrors } from '@/lib/fieldErrors';
import type { UpdateClientPayload } from '@/types/client';

const SAVE_ERROR = 'Не удалось сохранить клиента';

/** Field labels for a 400 body: {"telegram_username": ["..."]} -> "Telegram: ...". */
const FIELD_LABELS: Record<string, string> = {
  phone: 'Телефон',
  name: 'Имя',
  email: 'Email',
  notes: 'Заметки',
  telegram_username: 'Telegram',
};

/**
 * Body of the save request.
 * @param common edited common fields; null when they were not edited
 * @param username normalized username ("" clears); null when it was not changed
 */
export function buildClientCardPayload(
  common: UpdateClientPayload | null,
  username: string | null
): UpdateClientPayload {
  const payload: UpdateClientPayload = { ...(common ?? {}) };
  // The card never edits the numeric id.
  delete payload.telegram_id;
  if (username === null) delete payload.telegram_username;
  else payload.telegram_username = username;
  return payload;
}

interface UseClientCardSaveOptions {
  /** The request passed (the form is no longer dirty). */
  onSaved?: () => void;
}

export function useClientCardSave(
  clientId: string | undefined,
  { onSaved }: UseClientCardSaveOptions = {}
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: UpdateClientPayload) =>
      clientsApi.update(clientId!, payload),
    onSuccess: (_client, payload) => {
      queryClient.invalidateQueries({ queryKey: ['client', clientId] });
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      // The attention block and the CRM board (nested key) show the username too.
      if (payload.telegram_username !== undefined) {
        queryClient.invalidateQueries({ queryKey: ATTENTION_QUERY_KEY });
      }
      onSaved?.();
      toast.success('Клиент обновлен');
    },
    onError: (err) => {
      toast.error(formatFieldErrors(err, FIELD_LABELS, SAVE_ERROR));
    },
  });
}
